import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Flex, Grid, GridItem, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { DataScanningService } from 'api/services';
import { AddButton, OutlinedIconButton, Select } from 'components/DataEntry';
import { AppIcon } from 'components/Icons';
import { SyncRestartIcon } from 'components/Icons/ReactCustomIcons/SyncRestartIcon';
import { successToast as showSuccessToast, useToasts } from 'components/Toast';
import { DataProtectionSummary } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/DataProtectionSummary';
import { DataSummary } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/DataSummary';
import { DataTypesBySource } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/DataTypesBySource';
import { DistributionByDataLabels } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/DistributionByDataLabels';
import { DistributionByDocumentType } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/DistributionByDocumentTypes';
import { DistributionByTypesOfFiles } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/DistributionByTypesOfFiles';
import {
  getSnowflakeTableTarget,
  ManualColumnClassificationModal,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/ManualColumnClassificationModal';
import { SensitiveDataBreakdown } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/SensitiveDataBreakdown';
import { SnowflakeTableDataOverview } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/SnowflakeTableDataOverview';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';

import { isRepositoryResource } from './utils';

export const SensitiveData: FC = () => {
  const dispatch = useDispatch();
  const { errorToast } = useToasts();
  const {
    resourceType,
    resourceInsight,
    resourceDetails,
    resourceUuid,
    resourceAccountId,
    data,
  } = useSelector(selectActiveResource);
  const [manualClassificationMode, setManualClassificationMode] = useState<
    'add' | null
  >(null);
  const [byType, setByType] = useState<Record<string, any>>({
    label: 'By data type',
    value: 'Data',
  });
  const [isSyncingLabels, setIsSyncingLabels] = useState(false);
  const isRepository = isRepositoryResource(resourceType);
  const target = useMemo(
    () => getSnowflakeTableTarget(resourceDetails.data, resourceInsight.data),
    [resourceDetails.data, resourceInsight.data],
  );
  const refreshClassifications = useCallback(() => {
    dispatch(
      actions.getDataTypesBySource({
        q: { sourceUuid: resourceUuid },
      }),
    );
  }, [dispatch, resourceUuid]);

  const syncLabels = useCallback(async () => {
    if (!resourceAccountId || !resourceUuid) return;

    setIsSyncingLabels(true);
    try {
      await DataScanningService.DataTypes.syncLabels(
        { accountId: resourceAccountId },
        { params: { resource_uuid: resourceUuid } },
      );
      // Sync only runs on request; the old copy implied it was automatic.
      showSuccessToast({
        title: 'Label sync started. Snowflake tags will update shortly.',
      });
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      errorToast({
        title: 'Unable to sync labels.',
        description:
          typeof detail === 'string' ? detail : 'Please try again later.',
      });
    } finally {
      setIsSyncingLabels(false);
    }
  }, [errorToast, resourceAccountId, resourceUuid]);

  useEffect(() => {
    if (!!resourceInsight.data) {
      dispatch(
        actions.getDataLabels({
          q: {
            accountIds: [resourceAccountId],
            sourceUUIDs: [resourceUuid],
          },
        }),
      );
    }

    if (
      !!resourceInsight.data &&
      resourceType !== NativeResources.SnowflakeTables
    ) {
      dispatch(
        actions.getDataSummary({
          q: {
            accountIds: [resourceAccountId],
            sourceUUIDs: [resourceUuid],
          },
        }),
      );
      dispatch(
        actions.getDistributionByTypeOfFiles({
          q: {
            accountIds: [resourceAccountId],
            sourceUUIDs: [resourceUuid],
          },
        }),
      );
      if (resourceType === NativeResources.SimpleStorageService)
        dispatch(
          actions.getDistributionByDocumentType({
            q: {
              id: resourceUuid,
            },
          }),
        );
    }
  }, [
    dispatch,
    resourceInsight.data,
    resourceUuid,
    resourceAccountId,
    resourceType,
  ]);

  return (
    <Stack
      spacing={6}
      flex={1}
      h="full"
      minH={0}
      overflowY="auto"
      overflowX="hidden"
    >
      {resourceType === NativeResources.SnowflakeTables ? (
        <SnowflakeTableDataOverview />
      ) : (
        <Grid
          templateColumns="repeat(3, minmax(0, 1fr))"
          gap={4}
          h={56}
          minH={0}
          flexShrink={0}
        >
          <GridItem colSpan={1} minH={0} minW={0}>
            <DataSummary />
          </GridItem>
          {!isRepository && (
            <GridItem colSpan={1} minH={0} minW={0}>
              <DataProtectionSummary />
            </GridItem>
          )}
          {isRepository ? (
            <SensitiveDataBreakdown
              data={data.dataTypesBySource.data}
              resourceType={resourceType}
              isLoading={data.dataTypesBySource.isLoading}
            />
          ) : (
            <GridItem colSpan={1} minH={0} minW={0} overflow="hidden">
              {resourceType === NativeResources.SimpleStorageService ? (
                byType?.value === 'Data' ? (
                  <DistributionByTypesOfFiles />
                ) : (
                  <DistributionByDocumentType />
                )
              ) : (
                <DistributionByDataLabels />
              )}
            </GridItem>
          )}
        </Grid>
      )}
      {resourceType === NativeResources.SnowflakeTables && (
        <Flex justifyContent="flex-end" gap={2} flexShrink={0}>
          <AddButton
            label="Add classification"
            onClick={() => setManualClassificationMode('add')}
          />
          <OutlinedIconButton
            leftIcon={<SyncRestartIcon />}
            // Mention the account's saved skip tags, which this sync honours.
            tooltip="Sync labels for this table in Snowflake. Columns with the account's saved skip tags are left untagged."
            aria-label="Sync Labels"
            isDisabled={!resourceAccountId || !resourceUuid}
            isLoading={isSyncingLabels}
            onClick={syncLabels}
          >
            Sync Labels
          </OutlinedIconButton>
        </Flex>
      )}
      {resourceType === NativeResources.SimpleStorageService && (
        <Flex w="full" justifyContent="flex-end">
          <Box w={48}>
            <Select
              options={[
                { label: 'By data type', value: 'Data' },
                { label: 'By document type', value: 'Document' },
              ]}
              value={byType}
              onChange={v => setByType(v)}
              menuPortalTarget={document.body}
              showIconInValueContainer
              valueContainerIcon={<AppIcon />}
              showClearIndicator={false}
            />
          </Box>
        </Flex>
      )}

      <DataTypesBySource type={byType?.value} />
      {manualClassificationMode && (
        <ManualColumnClassificationModal
          isOpen
          onClose={() => setManualClassificationMode(null)}
          accountId={resourceAccountId}
          target={target}
          mode={manualClassificationMode}
          onSaved={refreshClassifications}
        />
      )}
    </Stack>
  );
};
